import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent } from "frontend-components";

export default function SurveyDescriptionBanner({ description }: { description: string | null }) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const descriptionRef = useRef<HTMLParagraphElement | null>(null);
    const descriptionId = "pulse-collapsible-description";
    const trimmedDescription = description?.trim() ?? "";

    useLayoutEffect(() => {
        const element = descriptionRef.current;

        if (!element || isExpanded) return;

        const updateOverflow = () => {
            setHasOverflow(element.scrollWidth > element.clientWidth);
        };

        updateOverflow();

        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isExpanded, trimmedDescription]);

    if (!trimmedDescription) return null;

    return (
        <Card className="bg-chameleon-yellow-10">
            <CardContent className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                    <div className="mt-1 h-7 w-7 shrink-0 rounded-md bg-surface-teal" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                            {t("responseOverview.descriptionSent")}
                        </p>
                        <p
                            ref={descriptionRef}
                            id={descriptionId}
                            className={
                                isExpanded
                                    ? "text-sm whitespace-pre-wrap wrap-break-word"
                                    : "truncate text-sm"
                            }
                        >
                            {trimmedDescription}
                        </p>
                    </div>
                </div>

                {isExpanded || hasOverflow ? (
                    <Button
                        type="button"
                        variant="ghost"
                        className="flex shrink-0 items-center gap-2 p-0 text-sm font-medium text-surface-teal"
                        onClick={() => setIsExpanded((open) => !open)}
                        aria-expanded={isExpanded}
                        aria-controls={descriptionId}
                    >
                        <span>
                            {isExpanded
                                ? t("responseOverview.close")
                                : t("responseOverview.readMore")}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={
                                isExpanded
                                    ? "rotate-180 transition-transform"
                                    : "transition-transform"
                            }
                        />
                    </Button>
                ) : null}
            </CardContent>
        </Card>
    );
}
